import { useState, useRef, useEffect } from 'react';

export default function FilterComboBox({ options, selected, onToggle, onClear, label, placeholder, t, si }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('mousedown', onClick); };
  }, []);

  // Dedupe options case-insensitively, strip empty/whitespace
  const deduped = [];
  const seen = new Set();
  for (const o of (options || [])) {
    const trimmed = (o || '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(trimmed);
  }
  const sorted = deduped.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const filtered = search ? sorted.filter(o => o.toLowerCase().includes(search.toLowerCase())) : sorted;

  const selectedSet = new Set((selected || []).map(s => s.toLowerCase()));

  function toggle(o) {
    onToggle(o);
    setSearch('');
    setHl(-1);
    // Keep dropdown open for multi-select
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHl(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && hl >= 0 && hl < filtered.length) { e.preventDefault(); toggle(filtered[hl]); }
    else if (e.key === 'Escape') { setOpen(false); }
    else if (e.key === 'Backspace' && !search && selected?.length) { onToggle(selected[selected.length - 1]); }
  }

  const hasSelected = selected?.length > 0;

  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: 360 }}>
      <div style={{ fontSize: 9, color: t.td, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{label}</div>
      {/* Chips + input row */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, border: `1px solid ${t.bd}`, borderRadius: 8, padding: '3px 6px', background: 'transparent', minHeight: 32, cursor: 'text' }} onClick={() => inputRef.current?.focus()}>
        {(selected || []).map(s => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.08)', border: `1px solid rgba(59,130,246,0.25)`, fontSize: 11, color: t.bl, whiteSpace: 'nowrap' }}>
            {s}
            <span onClick={e => { e.stopPropagation(); onToggle(s); }} style={{ cursor: 'pointer', fontSize: 10, color: t.bl, marginLeft: 1 }}>✕</span>
          </span>
        ))}
        <input
          ref={inputRef}
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); setHl(-1); }}
          onFocus={() => { setOpen(true); setHl(-1); }}
          onKeyDown={handleKey}
          placeholder={hasSelected ? '' : (placeholder || 'Search...')}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, padding: '3px 2px', flex: 1, minWidth: 60, fontFamily: si?.fontFamily || "'DM Sans',sans-serif", color: t?.tx || '#e8e9ed' }}
        />
        {hasSelected && (
          <span onClick={e => { e.stopPropagation(); onClear(); setSearch(''); }} style={{ cursor: 'pointer', fontSize: 10, color: t.td, padding: '0 2px', flexShrink: 0 }}>Clear all</span>
        )}
      </div>
      {/* Dropdown */}
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: t.cd, border: `1px solid ${t.bd}`, borderRadius: 8, maxHeight: 220, overflow: 'auto', zIndex: 15, marginTop: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 11, color: t.td, fontStyle: 'italic' }}>{search ? 'No matches' : 'No options'}</div>
          ) : filtered.map((o, i) => {
            const isSelected = selectedSet.has(o.toLowerCase());
            return (
              <div key={o} onMouseDown={e => { e.preventDefault(); toggle(o); }} style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 12, background: i === hl ? t.sfh : 'transparent', borderBottom: i < filtered.length - 1 ? `1px solid ${t.bd}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onMouseEnter={() => setHl(i)}>
                <span>{o}</span>
                {isSelected && <span style={{ color: t.gn, fontSize: 13, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
